import React from "react";
import {Badge} from "../ui/badge";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUser} from "@fortawesome/free-solid-svg-icons";
import {Check, Heart, ShoppingCart, Star} from "lucide-react";
import {money} from "../../lib/format";
import {useWishlist} from "../landing/useWishlist";
import {useLandingCartSync} from "../landing/useLandingCart";

const RATING_STARS = [0, 1, 2, 3, 4];

function Stars({rating}) {
  return (
    <span className="flex items-center gap-[1px]" aria-hidden="true">
      {RATING_STARS.map((i) => (
        <Star
          key={i}
          className="h-3 w-3"
          style={{
            fill: i < Math.round(rating) ? "#f59e0b" : "none",
            color: i < Math.round(rating) ? "#f59e0b" : "#d1d5db",
          }}
        />
      ))}
    </span>
  );
}

/**
 * Card used by the learner catalog grids (courses / books / journals).
 * Every action is optional, so the same card works for owned and un-owned items.
 *
 * When `courseId` is passed the card switches to the landing-marketplace course
 * layout: wishlist heart, star rating row, and price + Add to cart on one row
 * (wired to the shared platform cart). Books/journals keep the simpler layout.
 */
const ResourceCard = ({
  image,
  title,
  subtitle,
  badge,
  meta,
  price,
  isFree,
  owned,
  busy,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
  onOpen,
  courseId,
  rating,
  reviews,
  hours,
  level,
  oldPrice,
  bestseller,
  hot,
  cornerClass = "rounded-2xl",
  compact = false,
}) => {
  const isCourse = Boolean(courseId);
  const {has, toggle} = useWishlist();
  const {isInCart, addCourse} = useLandingCartSync();

  const wished = isCourse ? has(courseId) : false;
  const inCart = isCourse ? isInCart(courseId) : false;

  const handleAddToCart = () => {
    if (!isCourse) return;
    addCourse({
      id: courseId,
      title,
      instructor: subtitle,
      thumbnail: image,
      category: badge,
      hours,
      level,
      rating,
      price: isFree ? 0 : price,
    });
  };

  return (
    <article
      role={isCourse ? "link" : undefined}
      tabIndex={isCourse ? 0 : undefined}
      onClick={isCourse ? onOpen : undefined}
      onKeyDown={
        isCourse
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
      className={
        isCourse
          ? "group flex h-full cursor-pointer flex-col overflow-hidden rounded-sm border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-[0_20px_45px_-20px_rgba(15,23,42,0.28)]"
          : `flex h-full flex-col overflow-hidden ${cornerClass} border border-slate-200 bg-white shadow-sm transition-colors hover:border-blue-200`
      }
    >
      <button
        type="button"
        onClick={onOpen}
        className={`relative block w-full overflow-hidden bg-slate-100 ${
          isCourse ? "aspect-[16/10]" : compact ? "aspect-[2/1]" : "aspect-video"
        }`}
        aria-label={title}
      >
        <img
          src={image || "/images/course_placeholder.jpg"}
          alt={title}
          loading="lazy"
          decoding="async"
          className={`h-full w-full object-cover transition-transform ${
            isCourse
              ? "object-top duration-500 group-hover:scale-105"
              : "duration-300 group-hover:scale-105"
          }`}
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {bestseller ? (
            <span className="rounded-sm bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 shadow-sm">
              Bestseller
            </span>
          ) : null}
          {hot ? (
            <span className="rounded-sm bg-orange-500 px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm">
              Hot
            </span>
          ) : null}
          {owned ? (
            <span className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-semibold text-white">
              Owned
            </span>
          ) : null}
        </div>
        {badge && !bestseller && !hot ? (
          <span className="absolute right-3 top-3">
            <Badge className="max-w-[150px] truncate border-0 bg-blue-900 px-2.5 py-0.5 text-xs font-semibold text-white">
              {badge}
            </Badge>
          </span>
        ) : null}
        {isCourse ? (
          <button
            type="button"
            aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            aria-pressed={wished}
            onClick={(event) => {
              event.stopPropagation();
              toggle(courseId);
            }}
            className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border backdrop-blur transition-all active:scale-90 ${
              wished
                ? "border-rose-200 bg-rose-50 text-rose-500"
                : "border-white/70 bg-white/85 text-slate-400 hover:text-rose-500"
            }`}
          >
            <Heart className="h-4 w-4" style={{fill: wished ? "#f43f5e" : "none"}} />
          </button>
        ) : null}
      </button>

      <div className={`flex flex-1 flex-col ${isCourse ? "gap-1.5 p-4" : compact ? "p-3" : "p-4"}`}>
        <h3
          className={`m-0 line-clamp-2 ${
            isCourse
              ? "text-[15px] font-semibold leading-snug text-slate-800 transition-colors group-hover:text-blue-800"
              : "text-base font-semibold text-slate-900"
          }`}
        >
          {title}
        </h3>

        {isCourse && rating != null ? (
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-normal text-slate-500">
            <span className="font-semibold text-amber-700">{Number(rating).toFixed(1)}</span>
            <Stars rating={rating} />
            {reviews != null ? <span>({Number(reviews).toLocaleString()})</span> : null}
            {hours != null ? (
              <>
                <span className="text-slate-300">·</span>
                <span>{hours} total hours</span>
              </>
            ) : null}
            {level ? (
              <>
                <span className="text-slate-300">·</span>
                <span>{level}</span>
              </>
            ) : null}
          </div>
        ) : null}

        {subtitle ? (
          isCourse ? (
            <p className="m-0 line-clamp-1 text-xs font-normal text-slate-500">
              Instructors: <span className="text-slate-600">{subtitle}</span>
            </p>
          ) : (
            <p className="m-0 mt-1 flex items-center gap-1.5 truncate text-xs text-slate-500">
              <FontAwesomeIcon icon={faUser} className="h-3 w-3" />
              <span className="truncate">{subtitle}</span>
            </p>
          )
        ) : null}
        {meta ? <p className="m-0 mt-2 truncate text-xs text-slate-500">{meta}</p> : null}

        {isCourse ? (
          <div className="mt-auto flex items-center justify-between gap-2 pt-2">
            <div className="flex items-baseline gap-2">
              <span className="text-base font-bold text-slate-900">
                {isFree ? "Free" : price != null ? money(price) : ""}
              </span>
              {!isFree && oldPrice != null && oldPrice > price ? (
                <span className="text-xs text-slate-400 line-through">{money(oldPrice)}</span>
              ) : null}
            </div>
            <button
              type="button"
              disabled={inCart}
              aria-label={inCart ? "Already in cart" : `Add ${title} to cart`}
              onClick={(event) => {
                event.stopPropagation();
                handleAddToCart();
              }}
              className={`flex shrink-0 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-colors ${
                inCart
                  ? "cursor-default border border-blue-200 bg-blue-50 text-blue-800"
                  : "bg-blue-900 text-white hover:bg-blue-800 active:bg-blue-950"
              }`}
            >
              {inCart ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  In cart
                </>
              ) : (
                <>
                  <ShoppingCart className="h-3.5 w-3.5" />
                  Add to cart
                </>
              )}
            </button>
          </div>
        ) : (
          <>
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-sm font-bold text-slate-900">
                {isFree ? "Free" : price != null ? money(price) : ""}
              </span>
            </div>

            {primaryLabel || secondaryLabel ? (
              <div className="mt-auto grid grid-cols-2 gap-2 pt-3">
                {primaryLabel ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onPrimary}
                    className="inline-flex h-10 w-full min-w-0 items-center justify-center whitespace-nowrap rounded-md bg-blue-900 px-2 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {busy ? "Please wait…" : primaryLabel}
                  </button>
                ) : null}
                {secondaryLabel ? (
                  <button
                    type="button"
                    disabled={busy || secondaryDisabled}
                    onClick={onSecondary}
                    className={`inline-flex h-10 w-full min-w-0 items-center justify-center whitespace-nowrap rounded-md border px-2 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      secondaryDisabled
                        ? "border-blue-200 bg-blue-50 text-blue-800"
                        : "border-slate-200 text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                    }`}
                  >
                    {secondaryLabel}
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};

export default ResourceCard;
